import React, { useEffect, useState, useRef } from "react";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import "../Pages/CSS/Donation.css"; 

export default function DonationList() {
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);

  const certificateRefs = useRef({});

  useEffect(() => {
    const fetchDonations = async () => {
      setLoading(true);
      try {
        const db = getFirestore();
        const snap = await getDocs(collection(db, "donations"));
        const data = [];
        snap.forEach((doc) => data.push({ id: doc.id, ...doc.data() }));

        // Sort newest first
        data.sort((a, b) => {
          const tA = a.created?._seconds || a.created || 0;
          const tB = b.created?._seconds || b.created || 0;
          return tB - tA;
        });

        setDonations(data);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    fetchDonations();
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp) return new Date().toLocaleDateString("en-IN");
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const downloadCertificate = async (donation) => {
    const input = certificateRefs.current[donation.id];
    if (!input) return;
    setIsDownloading(true);
    try {
      const originalWidth = input.style.width;
      const originalTransform = input.style.transform;

      input.style.width = "900px";
      input.style.transform = "scale(1)";

      const canvas = await html2canvas(input, {
        scale: 5,
        useCORS: true,
        logging: false,
        backgroundColor: "#d6d3d3",
      });

      input.style.width = originalWidth;
      input.style.transform = originalTransform;

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a3",
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      pdf.addImage(imgData, "PNG", 0, 0, pageWidth, pageHeight);
      pdf.save(`Darvik-Certificate-${donation.id}.pdf`);
    } catch (err) {
      console.error(err);
      alert("Error generating certificate.");
    }
    setIsDownloading(false);
  };

  const YesNoBadge = (val) => {
    if (val === true) return <span className="badge bg-success">Yes</span>;
    if (val === false) return <span className="badge bg-danger">No</span>;
    return <span className="text-muted">—</span>;
  };

  return (
    <div className="card shadow-sm mb-5">
      <div className="card-header">
        <h4 className="mb-0">Donations Received</h4>
      </div>
      <div className="card-body px-2 py-3">
        {loading ? (
          <div className="text-center my-5">
            <span className="spinner-border text-info" /> Loading donation records...
          </div>
        ) : donations.length === 0 ? (
          <div className="text-center text-muted py-4">No donations received yet.</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-striped table-bordered">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Amount</th>
                  <th>PAN</th>
                  <th>Tax Exempt</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {donations.map((don, idx) => (
                  <tr key={don.id}>
                    <td>{idx + 1}</td>
                    <td>{don.name || <span className="text-secondary fst-italic">Anonymous</span>}</td>
                    <td>{don.email || <span className="text-muted">—</span>}</td>
                    <td>₹{don.amount}</td>
                    <td>{don.pan || <span className="text-muted">—</span>}</td>
                    <td>{YesNoBadge(don.taxExempt)}</td>
                    <td>{formatDate(don.created)}</td>
                    <td>
                      {/* Hidden certificate for generating PDF */}
                      <div
                        style={{ position: "absolute", left: "-9999px" }}
                        ref={(el) => (certificateRefs.current[don.id] = el)}
                      >
                        <div className="certificate" style={{ width: "900px", padding: "20px", background: "#fff" }}>
                          <div className="certificate-border">
                            <div className="certificate-header centered-header">
                              <div className="foundation-logo">
                                <img src="/logodarvik.png" alt="Darvik Foundation" />
                                <h2>Certificate of Appreciation</h2>
                              </div>
                              <div className="certificate-date">
                                Issued on: {formatDate(don.created)}
                              </div>
                            </div>
                            <div className="certificate-body">
                              <div className="presentation-text">This certificate is proudly presented to</div>
                              <div className="donor-name">{don.name}</div>
                              <div className="donation-details">
                                <p>In recognition of your generous contribution of</p>
                                <div className="donation-amount">₹{don.amount}</div>
                                <p>towards our mission of building a brighter future.</p>
                                {don.pan && <p>PAN: {don.pan}</p>}
                                <p>Tax Exemption: {don.taxExempt ? "Yes" : "No"}</p>
                              </div>
                              <div className="appreciation-text">
                                Your support enables us to continue our vital work and create lasting positive change in our community.
                              </div>
                            </div>
                            <div className="certificate-footer">
                              <div className="signature">
                                <div>Dhanesh Raveendranath</div>
                                <p>Founder, Darvik Foundation</p>
                              </div>
                              <div className="verification">
                                <p>Verification ID: {don.id}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => downloadCertificate(don)}
                        disabled={isDownloading}
                      >
                        {isDownloading ? "Downloading..." : "Download Certificate"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
